/**
 * Rewards Bootstrap - Tapcart Auth Gate
 * Prevents smile-rewards.js from loading until Shopify session is validated in Tapcart context
 * Preserves existing web behavior (immediate redirect to login if not authenticated)
 */

(function() {
  'use strict';

  // ============================================================================
  // GLOBAL NAMESPACE + STATE MACHINE
  // ============================================================================

  if (window.rewardsBootstrap) {
    // Already initialized, prevent double execution
    return;
  }

  window.rewardsBootstrap = {
    version: '1.0.0',
    state: 'idle',
    scriptInjected: false,
    initCalled: false,
    gateInFlight: false,
    lastGateStartTs: null,
    retry: retryGate
  };

  // ============================================================================
  // FLAGS + DEBUG DETECTION
  // ============================================================================

  const urlParams = new URLSearchParams(window.location.search);
  const flags = {
    forceImmediateSmile: urlParams.get('force_immediate_smile') === '1',
    disableTapcartGate: urlParams.get('disable_tapcart_gate') === '1',
    failOpenSmile: urlParams.get('fail_open_smile') === '1'
  };

  const isDebugEnabled = urlParams.get('tapcart_debug') === '1' || 
    (typeof localStorage !== 'undefined' && localStorage.getItem('__tapcart_debug') === 'true');

  // ============================================================================
  // DEBUG LOGGING
  // ============================================================================

  function debugLog() {
    if (isDebugEnabled) {
      console.log('[RewardsBootstrap]', ...arguments);
    }
  }

  // ============================================================================
  // TAPCART CONTEXT DETECTION
  // ============================================================================

  function isTapcartContext() {
    return document.documentElement.dataset.tapcart === 'true' ||
           window.__isTapcart === true ||
           !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.Tapcart) ||
           typeof window.CustomBlockJavascriptInterface !== 'undefined';
  }

  // ============================================================================
  // TAPCART SDK READINESS GATE
  // ============================================================================

  /**
   * Wait for Tapcart SDK to be fully initialized
   * @returns {Promise<boolean>} true if ready, false if timeout
   */
  async function waitForTapcartReady() {
    if (!window.Tapcart) return false;
    if (window.Tapcart.isInitialized === true) return true;
    
    return new Promise((resolve) => {
      let resolved = false;
      const cleanup = () => {
        if (resolved) return;
        resolved = true;
        clearTimeout(timeoutId);
        clearInterval(pollId);
        window.removeEventListener('webbridge-ready', onReady);
      };
      
      const onReady = () => {
        cleanup();
        resolve(true);
      };
      
      // Listen for webbridge-ready event (optional accelerator)
      window.addEventListener('webbridge-ready', onReady, { once: true });
      
      // Poll for isInitialized (fallback)
      const pollId = setInterval(() => {
        if (window.Tapcart && window.Tapcart.isInitialized === true) {
          cleanup();
          resolve(true);
        }
      }, 100);
      
      // Timeout after 10s - fail safe
      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(false);
      }, 10000);
    });
  }

  // ============================================================================
  // AUTH GATE - MIRROR BRIDGE HEURISTICS
  // ============================================================================

  async function runAuthGate() {
    if (window.rewardsBootstrap.gateInFlight) {
      debugLog('Gate already in flight, skipping');
      return { ok: false, reason: 'gate_in_flight', attempts: 0, durationMs: 0 };
    }

    window.rewardsBootstrap.gateInFlight = true;
    window.rewardsBootstrap.lastGateStartTs = Date.now();
    const startTime = Date.now();
    
    debugLog('Gate started, isTapcart:', isTapcartContext());

    const delays = [500, 1000, 2000, 3000, 4000];
    const globalTimeout = 15000;
    let attempts = 0;
    let authenticateAttempted = false;

    while (attempts < delays.length && (Date.now() - startTime) < globalTimeout) {
      attempts++;
      debugLog(`Probe attempt ${attempts}/${delays.length}`);

      try {
        const response = await fetch('/account', {
          method: 'GET',
          credentials: 'include',
          redirect: 'manual',
          cache: 'no-cache'
        });

        debugLog(`Probe attempt ${attempts} response: status=${response.status}, type=${response.type}`);

        // Success conditions (same as bridge)
        if (response.status === 200) {
          if (!response.url || !response.url.includes('/account/login')) {
            debugLog('Gate success: session valid');
            window.rewardsBootstrap.gateInFlight = false;
            return { ok: true, reason: 'session_valid', attempts, durationMs: Date.now() - startTime };
          }
          debugLog('Probe got login page, not ready');
        }

        // Failure conditions to retry (same as bridge)
        const shouldRetry = response.type === 'opaqueredirect' ||
                           response.status === 0 ||
                           (response.status >= 300 && response.status < 600);

        if (!shouldRetry) {
          debugLog(`Probe unexpected status ${response.status}, will retry`);
        }

      } catch (error) {
        debugLog(`Probe attempt ${attempts} failed:`, error.message);
      }

      // Try Tapcart.authenticate() once per gate run if available
      if (!authenticateAttempted && 
          window.Tapcart && 
          typeof window.Tapcart.authenticate === 'function') {
        try {
          debugLog('Waiting for Tapcart SDK to be ready...');
          const isReady = await waitForTapcartReady();
          if (!isReady) {
            debugLog('Tapcart SDK not ready, skipping authenticate');
            authenticateAttempted = true;
            // Exit gate early - show retry UI
            return { ok: false, reason: 'sdk_not_ready' };
          }
          
          debugLog('Attempting Tapcart.authenticate()');
          await window.Tapcart.authenticate();
          authenticateAttempted = true;
          debugLog('Tapcart.authenticate() completed');
          // Continue probing after authenticate
          continue;
        } catch (authError) {
          debugLog('Tapcart.authenticate() failed:', authError.message);
          authenticateAttempted = true;
        }
      }

      // Apply backoff delay
      if (attempts < delays.length) {
        const delay = delays[Math.min(attempts - 1, delays.length - 1)];
        debugLog(`Waiting ${delay}ms before next attempt`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    debugLog('Gate timeout after', Date.now() - startTime, 'ms');
    window.rewardsBootstrap.gateInFlight = false;
    return { ok: false, reason: 'timeout', attempts, durationMs: Date.now() - startTime };
  }

  // ============================================================================
  // NON-DESTRUCTIVE UI WRAPPER PATTERN
  // ============================================================================

  function createBootstrapUI() {
    if (document.getElementById('rewards-bootstrap-gate')) {
      debugLog('Bootstrap UI already exists');
      return;
    }

    debugLog('Creating bootstrap UI');

    // Create wrapper element
    const wrapper = document.createElement('div');
    wrapper.id = 'rewards-bootstrap-gate';
    wrapper.innerHTML = `
      <div class="rewards-gate-loading" id="rewards-gate-loading">
        <div style="text-align: center; padding: 40px 20px;">
          <div style="display: inline-block; width: 24px; height: 24px; border: 3px solid #e2e8f0; border-top: 3px solid #059669; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px;"></div>
          <p style="color: #64748b; font-size: 16px; margin: 0;">Authenticating...</p>
        </div>
      </div>
      <div class="rewards-gate-retry" id="rewards-gate-retry" style="display: none;">
        <div style="text-align: center; padding: 40px 20px;">
          <p style="color: #64748b; font-size: 16px; margin: 0 0 16px 0;">We're having some trouble logging you in... click here to try again</p>
          <button type="button" onclick="window.rewardsBootstrap.retry()" style="background: #059669; color: white; border: none; border-radius: 4px; padding: 12px 24px; font-size: 14px; cursor: pointer;">Reload</button>
        </div>
      </div>
    `;

    // Add spinner CSS
    if (!document.getElementById('rewards-bootstrap-styles')) {
      const style = document.createElement('style');
      style.id = 'rewards-bootstrap-styles';
      style.textContent = '@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }

    // Insert before #smile-rewards-root if present, otherwise at top of main or body
    const smileRoot = document.getElementById('smile-rewards-root');
    if (smileRoot) {
      smileRoot.parentNode.insertBefore(wrapper, smileRoot);
    } else {
      const main = document.querySelector('main') || document.body;
      main.insertBefore(wrapper, main.firstChild);
    }
  }

  function showLoadingState() {
    const loading = document.getElementById('rewards-gate-loading');
    const retry = document.getElementById('rewards-gate-retry');
    if (loading) loading.style.display = 'block';
    if (retry) retry.style.display = 'none';
  }

  function showRetryState() {
    const loading = document.getElementById('rewards-gate-loading');
    const retry = document.getElementById('rewards-gate-retry');
    if (loading) loading.style.display = 'none';
    if (retry) retry.style.display = 'block';
    debugLog('Showing retry state');
  }

  function removeBootstrapUI() {
    const wrapper = document.getElementById('rewards-bootstrap-gate');
    if (wrapper) {
      wrapper.remove();
      debugLog('Bootstrap UI removed');
    }
  }

  // ============================================================================
  // DYNAMIC SCRIPT INJECTION + MANUAL INIT
  // ============================================================================

  function injectSmileRewards() {
    if (window.rewardsBootstrap.scriptInjected || document.getElementById('smile-rewards-injected')) {
      debugLog('Smile already injected, skipping');
      return;
    }

    debugLog('Injecting smile-rewards.js');
    window.rewardsBootstrap.state = 'injecting';

    const script = document.createElement('script');
    script.id = 'smile-rewards-injected';
    script.src = window.__smileRewardsSrc || '/assets/smile-rewards.js';
    script.async = true;

    script.onload = function() {
      debugLog('smile-rewards.js loaded successfully');
      window.rewardsBootstrap.scriptInjected = true;
      manualInitOnce();
    };

    script.onerror = function() {
      console.error('[RewardsBootstrap] Failed to load smile-rewards.js from:', script.src);
      debugLog('Script injection failed');
      const wrapper = document.getElementById('rewards-bootstrap-gate');
      if (wrapper) {
        wrapper.innerHTML = '<div style="text-align: center; padding: 40px 20px;"><p style="color: #ef4444;">Couldn\'t load Rewards. Please refresh.</p></div>';
      }
    };

    document.head.appendChild(script);
  }

  function manualInitOnce() {
    if (window.rewardsBootstrap.initCalled) {
      debugLog('Init already called, skipping');
      return;
    }

    if (window.vitableRewards && typeof window.vitableRewards.initRewardsPage === 'function') {
      try {
        debugLog('Calling vitableRewards.initRewardsPage()');
        window.rewardsBootstrap.initCalled = true;
        window.rewardsBootstrap.state = 'injected';
        window.vitableRewards.initRewardsPage();
        debugLog('Manual init completed successfully');
      } catch (error) {
        debugLog('Manual init failed:', error.message);
      }
    } else {
      debugLog('vitableRewards.initRewardsPage not available');
    }
  }

  // ============================================================================
  // RETRY FUNCTIONALITY
  // ============================================================================

  async function retryGate() {
    debugLog('Retry button clicked');
    
    if (window.rewardsBootstrap.gateInFlight) {
      debugLog('Gate already in flight, ignoring retry');
      return;
    }

    window.rewardsBootstrap.state = 'gating';
    showLoadingState();

    const result = await runAuthGate();
    
    if (result.ok) {
      debugLog('Retry gate success');
      removeBootstrapUI();
      if (!window.rewardsBootstrap.scriptInjected) {
        injectSmileRewards();
      }
    } else {
      debugLog('Retry gate failed:', result.reason);
      showRetryState();
      
      // Handle fail_open_smile flag on retry
      if (flags.failOpenSmile && !window.rewardsBootstrap.scriptInjected) {
        debugLog('fail_open_smile enabled, injecting Smile anyway');
        injectSmileRewards();
      }
    }
  }

  // ============================================================================
  // BOOTSTRAP ENTRYPOINT
  // ============================================================================

  async function bootstrap() {
    debugLog('Bootstrap starting, flags:', flags, 'isTapcart:', isTapcartContext());

    // Guard against multiple execution
    if (window.rewardsBootstrap.state !== 'idle') {
      debugLog('Already processed, current state:', window.rewardsBootstrap.state);
      return;
    }

    // Flag precedence #1: force_immediate_smile (any context)
    if (flags.forceImmediateSmile) {
      debugLog('force_immediate_smile=1, injecting Smile immediately');
      removeBootstrapUI();
      injectSmileRewards();
      return;
    }

    // Non-Tapcart context: preserve existing behavior (no gate UI)
    if (!isTapcartContext()) {
      debugLog('Non-Tapcart context, injecting Smile immediately');
      window.rewardsBootstrap.state = 'injecting';
      injectSmileRewards();
      return;
    }

    // Tapcart context from here
    debugLog('Tapcart context detected');

    // Flag precedence #2: disable_tapcart_gate (Tapcart only)
    if (flags.disableTapcartGate) {
      debugLog('disable_tapcart_gate=1, bypassing gate in Tapcart');
      injectSmileRewards();
      return;
    }

    // Run auth gate in Tapcart context
    window.rewardsBootstrap.state = 'gating';
    createBootstrapUI();
    showLoadingState();

    const result = await runAuthGate();

    if (result.ok) {
      debugLog('Gate success, removing UI and injecting Smile');
      window.rewardsBootstrap.state = 'injecting';
      removeBootstrapUI();
      injectSmileRewards();
    } else {
      debugLog('Gate failed:', result.reason, 'attempts:', result.attempts, 'duration:', result.durationMs);
      window.rewardsBootstrap.state = 'timeout_failed';
      showRetryState();
      
      // Flag precedence #3: fail_open_smile (diagnostic mode)
      if (flags.failOpenSmile) {
        debugLog('fail_open_smile=1, injecting Smile despite timeout');
        injectSmileRewards();
      }
    }
  }

  // ============================================================================
  // DELAYED EXECUTION
  // ============================================================================

  // Add 5s initial delay before starting bootstrap, matching account bridge pattern
  debugLog('Starting 5s initial loading delay...');
  setTimeout(function() {
    debugLog('Initial 5s delay completed, starting rewards bootstrap');
    bootstrap().catch(function(error) {
      debugLog('Bootstrap failed with error:', error.message);
    });
  }, 5000);

})();